import { CALL_CLEANUP_DELAY_MS } from 'Modules/softphone/constants';
import { AudioService } from 'Modules/softphone/services/AudioService';
import { WebRTCService } from 'Modules/softphone/services/WebRTCService';
import { useSoftphoneStore } from 'Modules/softphone/store';
import { createLogger } from 'Modules/softphone/utils/logger';

const logger = createLogger('CallManager:SessionCleanupManager');

export class SessionCleanupManager {
  private pendingTimeouts: Map<string, ReturnType<typeof setTimeout>> =
    new Map();

  private sessionListeners: Map<string, () => void> = new Map();

  /**
   * Schedules cleanup for a specific call after a configurable delay.
   *
   * This method is called when a call terminates (hangup, reject, BYE received, etc.)
   * to ensure all resources are properly released. The delay allows for any final
   * SIP signaling to complete before cleanup.
   *
   * **Actions performed after delay:**
   * - Removes the call from the softphone store
   * - Cleans up the WebRTC peer connection
   * - Removes the remote audio stream
   * - Removes the session listener
   *
   * @param callId - The unique identifier of the call to cleanup
   * @param webrtcService - The WebRTC service instance for peer connection cleanup
   * @param audioService - The audio service instance for stream cleanup
   *
   * @remarks
   * - If a cleanup is already scheduled for this call, it will be cancelled and rescheduled
   * - The delay is configured via {@link CALL_CLEANUP_DELAY_MS} (currently 100ms)
   * - This is for cleaning up a **single terminated call**, not the entire manager
   * ```
   *
   * @see {@link cleanup} for cleaning up the entire manager while preserving active calls
   */
  scheduleCallCleanup(
    callId: string,
    webrtcService: WebRTCService,
    audioService: AudioService
  ): void {
    logger.info('Scheduling call cleanup', { callId });

    // Clear any existing timeout for this call
    const existingTimeout = this.pendingTimeouts.get(callId);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
      logger.debug('Cleared existing cleanup timeout', { callId });
    }

    const timeout = setTimeout(() => {
      logger.info('Executing call cleanup', { callId });

      const store = useSoftphoneStore.getState();
      const activeCallsBeforeRemoval = store.activeCalls.size;

      // If the call belongs to an active conference, only clean up
      // WebRTC/audio resources but keep the call in the store as a virtual
      // participant. The call will be removed when the entire conference is
      // cleared via clearConference().
      const call = store.activeCalls.get(callId);
      if (call?.conferenceId && store.activeConference) {
        logger.info('Conference participant cleanup — preserving call in store', {
          callId,
          conferenceId: call.conferenceId,
        });
        webrtcService.cleanup(callId);
        audioService.setRemoteStream(null);
        this.cleanupSessionListener(callId);
        this.pendingTimeouts.delete(callId);
        return;
      }

      store.removeCall(callId);
      webrtcService.cleanup(callId);
      audioService.setRemoteStream(null);

      // Cleanup listener
      this.cleanupSessionListener(callId);

      // Remove from pending timeouts
      this.pendingTimeouts.delete(callId);

      const activeCallsAfterRemoval =
        useSoftphoneStore.getState().activeCalls.size;
      logger.info('Call cleanup complete', {
        callId,
        activeCallsBefore: activeCallsBeforeRemoval,
        activeCallsAfter: activeCallsAfterRemoval,
      });
    }, CALL_CLEANUP_DELAY_MS);

    this.pendingTimeouts.set(callId, timeout);
  }

  /**
   * Cleanup session listener for a call
   */
  cleanupSessionListener(callId: string): void {
    const removeListener = this.sessionListeners.get(callId);
    if (removeListener) {
      removeListener();
      this.sessionListeners.delete(callId);
      logger.debug('Cleaned up session listener for:', { callId });
    }
  }

  /**
   * Add session listener
   */
  addSessionListener(callId: string, removeListener: () => void): void {
    this.sessionListeners.set(callId, removeListener);
  }

  /**
   * Get session listener
   */
  getSessionListener(callId: string): (() => void) | undefined {
    return this.sessionListeners.get(callId);
  }

  /**
   * Cleans up all pending resources while preserving active calls.
   *
   * This method is called during manager teardown (e.g., logout, ConnectionManager destruction)
   * to release resources without affecting calls that are still in progress.
   *
   * **What gets cleaned up:**
   * - Pending cleanup timeouts for inactive calls
   * - Session listeners for inactive calls
   *
   * **What is preserved:**
   * - Cleanup timeouts for calls in `activeCallIds`
   * - Session listeners for calls in `activeCallIds`
   *
   * @param activeCallIds - Set of call IDs that should NOT be cleaned up
   *
   * @remarks
   * - This performs **immediate** cleanup, unlike {@link scheduleCallCleanup} which uses a delay
   * - Safe to call multiple times
   * - Logs warnings but doesn't throw on individual cleanup failures
   * ```
   *
   * @see {@link scheduleCallCleanup} for cleaning up a single terminated call
   */
  cleanup(activeCallIds: Set<string>): void {
    // Clear pending timeouts ONLY for calls that are NOT active
    if (this.pendingTimeouts && this.pendingTimeouts.size > 0) {
      try {
        const timeoutsToRemove: string[] = [];
        this.pendingTimeouts.forEach((timeout, callId) => {
          if (!activeCallIds.has(callId)) {
            try {
              if (timeout !== null && timeout !== undefined) {
                clearTimeout(timeout);
              }
              timeoutsToRemove.push(callId);
            } catch (e) {
              logger.warn('Error clearing timeout for call', {
                callId,
                error: (e as Error).message,
              });
            }
          } else {
            logger.debug('Preserving cleanup timeout for active call', {
              callId,
            });
          }
        });
        timeoutsToRemove.forEach((callId) =>
          this.pendingTimeouts.delete(callId)
        );
      } catch (e) {
        logger.warn('Error iterating pending timeouts', {
          error: (e as Error).message,
        });
      }
    }

    // Cleanup session listeners ONLY for calls that are NOT active
    if (this.sessionListeners && this.sessionListeners.size > 0) {
      try {
        const listenersToRemove: string[] = [];
        this.sessionListeners.forEach((removeListener, callId) => {
          if (!activeCallIds.has(callId)) {
            try {
              if (
                removeListener !== null &&
                typeof removeListener === 'function'
              ) {
                removeListener();
              }
              listenersToRemove.push(callId);
            } catch (e) {
              logger.warn('Error removing session listener for call', {
                callId,
                error: (e as Error).message,
              });
            }
          } else {
            logger.debug('Preserving session listener for active call', {
              callId,
            });
          }
        });
        listenersToRemove.forEach((callId) =>
          this.sessionListeners.delete(callId)
        );
      } catch (e) {
        logger.warn('Error iterating session listeners', {
          error: (e as Error).message,
        });
      }
    }
  }
}
